import React, { useState, useEffect } from "react";

import { Box, Typography } from "@mui/material";
import { Timeline } from "@mui/lab";

import CustomTimelineItem from "../Components/relatedListViewComponents/CustomTimelineItem";
import PageLoader from "../Components/PageLoader";
import { fetchRecordHistory } from "../utils/api";
import { fetchExtensionSettings } from "../utils/zoho";
import { unflattenDbRecords, filterChangedRecordFields } from "../utils/records";

const ZOHO = window.ZOHO;

export default function RelatedList() {
  const [moduleInfo, setModuleInfo] = useState(null);
  const [zohoLoaded, setZohoLoaded] = useState(false);
  const [loading, setLoading] = useState(true);

  const [zohoApiKey, setZohoApiKey] = useState();
  const [orgId, setOrgId] = useState();
  const [dataCenterUrl, setDataCenterUrl] = useState("");
  const [dbConnUrl, setDbConnUrl] = useState();

  const [modifiedRecordInfo, setModifiedRecordInfo] = useState(null);
  const [fieldsLabel, setFieldsLabel] = useState(null);
  const [unflattenFetchedRecords, setUnflattenFetchedRecords] = useState(null);
  const [recordResp, setRecordResp] = useState(null);

  useEffect(() => {
    async function initZoho() {
      ZOHO.embeddedApp.on("PageLoad", async function (data) {
        setModuleInfo(data);
        ZOHO.CRM.UI.Resize({ height: "700", width: "1000" });

        const settings = await fetchExtensionSettings({ includeDbUrl: true });
        setDataCenterUrl(settings.dataCenterUrl);
        setZohoApiKey(settings.apiKey);
        setDbConnUrl(settings.dbConnectionUrl);
        setOrgId(settings.orgId);

        setZohoLoaded(true);
      });
      ZOHO.embeddedApp.init();
    }
    initZoho();
  }, []);

  useEffect(() => {
    if (zohoLoaded && orgId && zohoApiKey) {
      const auth = { orgId, apiKey: zohoApiKey };

      async function fetchRecord() {
        const moduleName = moduleInfo.Entity;
        const recordId = `${moduleInfo.EntityId}`;

        const record_response = await ZOHO.CRM.API.getRecord({
          Entity: moduleName,
          approved: "both",
          RecordID: recordId,
        });

        setRecordResp(record_response?.data?.[0]);

        const history = await fetchRecordHistory(auth, {
          moduleName,
          recordId,
          connectionUrl: dbConnUrl,
        });

        const unflattenRecordFromDb = unflattenDbRecords(history.data);
        setUnflattenFetchedRecords(unflattenRecordFromDb);
        setModifiedRecordInfo(filterChangedRecordFields(unflattenRecordFromDb));
      }

      async function getFieldsLabel() {
        const getResponse = await ZOHO.CRM.META.getFields({
          Entity: moduleInfo.Entity,
        });
        const field_label_against_api_name = {};
        getResponse.fields.forEach((field) => {
          field_label_against_api_name[field.api_name] = field.display_label;
        });

        return field_label_against_api_name;
      }

      (async () => {
        try {
          const fieldsLabel = await getFieldsLabel();
          setFieldsLabel(fieldsLabel);
          if (dbConnUrl) {
            await fetchRecord();
          }
          setLoading(false);
        } catch (err) {
          console.log({ err });
        }
      })();
    }
  }, [zohoLoaded, zohoApiKey, orgId, dbConnUrl, moduleInfo]);

  if (loading || !modifiedRecordInfo || !recordResp) {
    return <PageLoader fullScreen message="Loading record history..." />;
  }

  if (modifiedRecordInfo && modifiedRecordInfo?.length <= 1) {
    return (
      <Box sx={{ width: "100%", height: "100%", p: "8px 24px", mt: 14 }}>
        <Typography sx={{ textAlign: "center", fontWeight: "bold" }}>
          No Database History Available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: "100%", p: "8px 24px" }}>
      <Typography
        sx={{ mb: "8px", fontSize: "20px", fontWeight: "500", ml: "16px" }}
      >
        Record History
      </Typography>

      <Timeline
        sx={{
          "& .MuiTimeline-root": {
            padding: 0,
          },
          "& .MuiTimelineItem-root:before": {
            flex: 0,
            padding: "0px",
          },
          width: "100%",
        }}
      >
        {modifiedRecordInfo?.map((backupRecord, index) => {
          return (
            <CustomTimelineItem
              key={backupRecord?.id}
              current_value={backupRecord}
              next_value={unflattenFetchedRecords[index + 1]}
              fieldsLabel={fieldsLabel}
              latestValue={recordResp}
              record_id={moduleInfo.EntityId}
            />
          );
        })}
      </Timeline>
    </Box>
  );
}
